import React, { useMemo, useState } from 'react'
import { DatePicker, Segmented, Space, Typography } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import { Granularity, MetricsPeriod } from '../../Actions/operationsMetricsApi'
import withTitleMetaData from '../../hocs/withTitleMetaData'
import RevenueSection from './components/RevenueSection'
import PaymentsSection from './components/PaymentsSection'
import CreditsSection from './components/CreditsSection'
import UsageSection from './components/UsageSection'
import FunnelSection from './components/FunnelSection'
import RetentionSection from './components/RetentionSection'

const { RangePicker } = DatePicker

// 눈에 보이는 빠른 기간 선택 — 달력을 열지 않고 한 번에 (custom은 직접설정)
const QUICK_RANGE_OPTIONS: { label: string; value: number | 'custom' }[] = [
  { label: '7일', value: 7 },
  { label: '30일', value: 30 },
  { label: '90일', value: 90 },
  { label: '180일', value: 180 },
  { label: '직접설정', value: 'custom' },
]

const rangeOfDays = (days: number): [Dayjs, Dayjs] => [dayjs().subtract(days - 1, 'day'), dayjs()]

const GRANULARITY_OPTIONS = [
  { label: '일', value: 'DAY' },
  { label: '주', value: 'WEEK' },
  { label: '월', value: 'MONTH' },
]

// 마케터가 분석마다 조절하는 레버 — 채널별 전환 속도가 달라 윈도우를 바꿔 본다
const WINDOW_OPTIONS = [
  { label: '3일', value: 3 },
  { label: '7일', value: 7 },
  { label: '14일', value: 14 },
  { label: '30일', value: 30 },
]

const PAID_WINDOW_OPTIONS = [
  { label: '14일', value: 14 },
  { label: '30일', value: 30 },
  { label: '60일', value: 60 },
  { label: '90일', value: 90 },
]

const MAX_WEEKS_OPTIONS = [
  { label: '8주', value: 8 },
  { label: '12주', value: 12 },
  { label: '16주', value: 16 },
]

/**
 * 운영지표 대시보드 (이슈 #239, 백엔드 aivatar#283 Phase 1)
 * 기간/단위 필터는 페이지 전역 — 5개 섹션 쿼리에 공통 적용, 섹션별 독립 로딩.
 */
type Tab = 'ops' | 'marketing'

const TAB_OPTIONS = [
  { label: '운영', value: 'ops' },
  { label: '마케팅', value: 'marketing' },
]

function OperationsMetrics() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => rangeOfDays(30))
  const [rangeMode, setRangeMode] = useState<number | 'custom'>(30)
  const [granularity, setGranularity] = useState<Granularity>('DAY')
  const [tab, setTab] = useState<Tab>('ops')

  const handleQuickRange = (value: string | number) => {
    setRangeMode(value as number | 'custom')
    if (value !== 'custom') setRange(rangeOfDays(value as number))
  }
  // 마케팅 탭 전용 레버
  const [windowDays, setWindowDays] = useState(7)
  const [paidWindowDays, setPaidWindowDays] = useState(30)
  const [maxWeeks, setMaxWeeks] = useState(12)

  // from/to는 항상 명시적으로 전달 — 백엔드 기본값(서버 타임존 now 기준 31일)에 의존하지 않는다
  const { period, prevPeriod } = useMemo(() => {
    const [from, to] = range
    const days = to.diff(from, 'day') + 1
    const prevTo = from.subtract(1, 'day')
    return {
      period: { from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') } as MetricsPeriod,
      prevPeriod: {
        from: prevTo.subtract(days - 1, 'day').format('YYYY-MM-DD'),
        to: prevTo.format('YYYY-MM-DD'),
      } as MetricsPeriod,
    }
  }, [range])

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }} data-build="ops-metrics-243">

      <Space wrap>
        <Segmented options={QUICK_RANGE_OPTIONS} value={rangeMode} onChange={handleQuickRange} />
        {rangeMode === 'custom' && (
          <RangePicker
            value={range}
            allowClear={false}
            onChange={(dates) => {
              if (dates?.[0] && dates?.[1]) setRange([dates[0], dates[1]])
            }}
          />
        )}
        <Typography.Text strong>
          {period.from} ~ {period.to}
        </Typography.Text>
        {tab === 'ops' && (
          <Segmented
            options={GRANULARITY_OPTIONS}
            value={granularity}
            onChange={(value) => setGranularity(value as Granularity)}
          />
        )}
      </Space>
      <Typography.Text type="secondary">시작·종료일 모두 포함 · KPI ▲▼는 직전 동일 길이 기간 대비</Typography.Text>
      <Segmented options={TAB_OPTIONS} value={tab} onChange={(value) => setTab(value as Tab)} />
      {tab === 'ops' ? (
        <>
          <RevenueSection period={period} prevPeriod={prevPeriod} />
          <PaymentsSection period={period} granularity={granularity} />
          <CreditsSection period={period} granularity={granularity} />
          <UsageSection period={period} />
        </>
      ) : (
        <>
          <Space wrap>
            <Typography.Text type="secondary">활성화 기준</Typography.Text>
            <Segmented options={WINDOW_OPTIONS} value={windowDays} onChange={(v) => setWindowDays(v as number)} />
            <Typography.Text type="secondary">유료전환 기준</Typography.Text>
            <Segmented
              options={PAID_WINDOW_OPTIONS}
              value={paidWindowDays}
              onChange={(v) => setPaidWindowDays(v as number)}
            />
            <Typography.Text type="secondary">리텐션</Typography.Text>
            <Segmented options={MAX_WEEKS_OPTIONS} value={maxWeeks} onChange={(v) => setMaxWeeks(v as number)} />
          </Space>
          <FunnelSection period={period} windowDays={windowDays} paidWindowDays={paidWindowDays} />
          <RetentionSection period={period} maxWeeks={maxWeeks} />
        </>
      )}
    </Space>
  )
}

export default withTitleMetaData(OperationsMetrics)
